import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Eye, EyeOff, Save, Printer, RefreshCw, Trash2, Plus, Upload, CheckCircle2,
} from "lucide-react";
import { api, API_BASE, errorToMessage, getStoredAuth } from "@/lib/apiClient";
import { useAuth, canCasesDelete } from "@/lib/auth";
import { CASES_V2 } from "@/constants/testIds";

const STATUS_COLORS = {
  PENDING_RECEPTION:            "bg-slate-100 text-slate-700",
  WITH_WAREHOUSE:               "bg-indigo-100 text-indigo-800",
  WITH_QC:                      "bg-violet-100 text-violet-800",
  WITH_BATTERY:                 "bg-fuchsia-100 text-fuchsia-800",
  WITH_RECEPTION_FOR_DISPATCH:  "bg-amber-100 text-amber-800",
  SHIPPED:                      "bg-sky-100 text-sky-800",
  DELIVERED:                    "bg-emerald-100 text-emerald-800",
  CLOSED:                       "bg-emerald-100 text-emerald-800",
  NON_REPAIRABLE:               "bg-rose-100 text-rose-800",
  WARRANTY_REJECTED:            "bg-rose-100 text-rose-800",
};

const CONTENT_TYPES = ["laptop", "charger", "bag", "battery", "mouse", "dongle", "power_cable", "other"];
const CONDITION_ISSUES = [
  "scratch", "dent", "cracked_screen", "broken_hinge", "keyboard_missing_key",
  "chipped_body", "sticker_residue", "liquid_marks", "yellowed", "other",
];
const PHYSICAL_DAMAGE_TYPES = [
  { v: "cracked_screen",   l: "Cracked Screen" },
  { v: "broken_hinge",     l: "Broken Hinge" },
  { v: "liquid_damage",    l: "Liquid Damage" },
  { v: "burn_marks",       l: "Burn Marks" },
  { v: "bent_body",        l: "Bent Body" },
  { v: "broken_keyboard",  l: "Broken Keyboard" },
  { v: "missing_parts",    l: "Missing Parts" },
  { v: "tampered_seal",    l: "Tampered Seal" },
  { v: "physical_impact",  l: "Physical Impact" },
  { v: "other",            l: "Other (Specify)" },
];
const WAREHOUSE_STATUSES = [
  "received", "diagnosis_started", "repair_in_progress", "motherboard_sent",
  "part_awaited", "repair_completed", "non_repairable", "warranty_rejected", "ready_for_qc",
];
const PHOTO_ANGLES = ["front", "back", "keyboard", "bottom", "accessories", "extra"];
const DISPATCH_MODES = [
  { v: "walkin", l: "Walk-in" },
  { v: "courier", l: "Courier" },
  { v: "porter", l: "Porter" },
  { v: "uncle", l: "Uncle Delivery" },
  { v: "company_vehicle", l: "Company Vehicle" },
  { v: "other", l: "Other" },
];

const inputCls = "w-full rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-slate-800";
const btnPrimary = "inline-flex items-center gap-1 rounded-md bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-40";
const btnGhost = "inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50";

function Chip({ text, cls, testId }) {
  return (
    <span data-testid={testId}
      className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${cls || "bg-slate-100 text-slate-700"}`}>
      {text}
    </span>
  );
}

function Section({ title, children, right }) {
  return (
    <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
        {right}
      </div>
      {children}
    </section>
  );
}

function TabNav({ tabs, active, onChange }) {
  return (
    <div className="flex flex-wrap gap-1 border-b border-slate-200">
      {tabs.map((t) => (
        <button key={t.key} data-testid={t.testId} onClick={() => onChange(t.key)}
          className={`px-3 py-2 text-sm rounded-t-md border-b-2 -mb-px ${
            active === t.key
              ? "border-slate-900 text-slate-900 font-semibold"
              : "border-transparent text-slate-500 hover:text-slate-800"
          }`}>
          {t.label}
        </button>
      ))}
    </div>
  );
}

export default function CaseV2DetailPage() {
  const { case_id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("overview");
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState(null);
  // Delete Case — SUPER_ADMIN + RECEPTION. Confirmation dialog + backend
  // enforcement; failures land in `deleteError` shown inside the modal.
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const { data } = await api.get(`/cases-v2/${case_id}`);
      setDetail(data);
    } catch (e) {
      setError(errorToMessage(e, "Failed to load case"));
    } finally { setLoading(false); }
  }, [case_id]);

  useEffect(() => { load(); }, [load]);

  const runAction = async (fn, { silent } = {}) => {
    setBusy(true); setError(null);
    try { await fn(); await load(); if (!silent) setFlash("Saved."); setTimeout(() => setFlash(null), 2500); }
    catch (e) { setError(errorToMessage(e, "Action failed")); }
    finally { setBusy(false); }
  };

  const performDelete = async () => {
    if (deleting) return;
    setDeleting(true);
    setDeleteError("");
    try {
      await api.delete(`/cases-v2/${detail.id}`);
      navigate("/admin/cases", { replace: true });
    } catch (e) {
      setDeleteError(errorToMessage(e, "Could not delete case"));
    } finally {
      setDeleting(false);
    }
  };

  if (loading) return <div className="p-10 text-slate-400">Loading case…</div>;
  if (error && !detail) return <div className="p-6 text-rose-700">{error}</div>;
  if (!detail) return <div className="p-6">Case not found.</div>;

  const status = detail.status || "OPEN";
  const readOnly = status === "CLOSED";

  return (
    <div className="space-y-6 pb-10" data-testid={CASES_V2.detailPage}>
      <div className="flex items-center justify-between">
        <div>
          <button onClick={() => navigate("/admin/cases")}
            className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 mb-2">
            <ArrowLeft size={14} /> Cases
          </button>
          <div className="flex items-center gap-3 flex-wrap" data-testid={CASES_V2.detailHeader}>
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900 font-mono">{detail.case_no}</h1>
            <Chip text={status.replaceAll("_", " ")} cls={STATUS_COLORS[status]} testId={CASES_V2.detailStatusPill} />
            {detail.has_battery_problem && (
              <Chip text="Battery Problem" cls="bg-fuchsia-100 text-fuchsia-800" />
            )}
            {detail.physical_damage && (
              <Chip text="Physical Damage" cls="bg-rose-100 text-rose-800" />
            )}
            {detail.warranty_authorization_status === "pending" && (
              <Chip text="Warranty Authorization Pending" cls="bg-amber-100 text-amber-800 border border-amber-300" />
            )}
            {detail.warranty_authorization_status === "approved" && (
              <Chip text="Warranty Approved" cls="bg-emerald-100 text-emerald-800" />
            )}
            {detail.warranty_authorization_status === "rejected" && (
              <Chip text="Warranty Rejected (Paid)" cls="bg-rose-100 text-rose-800" />
            )}
            {detail.customer_source && (
              <span className="text-xs text-slate-500">{detail.customer_source.replaceAll("_", " ")}</span>
            )}
          </div>
          <p className="text-sm text-slate-600 mt-1">
            {detail.customer_name || "—"} · <span className="font-mono">{detail.mobile}</span>
            {detail.brand && <> · {detail.brand} {detail.model}</>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid={CASES_V2.printLabelBtn}
            onClick={() => window.open(`/print/label/${detail.id}`, "_blank")}
            className={btnGhost}>
            <Printer size={14} /> Print Label
          </button>
          <button data-testid={CASES_V2.printReceiptBtn}
            onClick={() => window.open(`/print/receipt/${detail.id}`, "_blank")}
            className={btnGhost}>
            <Printer size={14} /> Print Receipt (A4)
          </button>
          <button onClick={load} className={btnGhost}><RefreshCw size={14} /> Reload</button>
          {canCasesDelete(user) && (
            <button
              type="button"
              data-testid="case-delete-btn"
              onClick={() => { setDeleteError(""); setConfirmDelete(true); }}
              className="inline-flex items-center gap-1 rounded-md border border-rose-300 bg-white px-3 py-2 text-sm text-rose-700 hover:bg-rose-50">
              <Trash2 size={14} /> Delete Case
            </button>
          )}
        </div>
      </div>

      {flash && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-700">
          {flash}
        </div>
      )}
      {error && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-700">
          {error}
        </div>
      )}

      {(detail.warranty_authorization_status === "pending"
        || detail.warranty_authorization_status === "approved"
        || detail.warranty_authorization_status === "rejected") && (
        <WarrantyAuthorizationCard
          detail={detail}
          user={user}
          busy={busy}
          runAction={runAction}
        />
      )}

      <TabNav
        active={tab} onChange={setTab}
        tabs={[
          { key: "overview",  label: "Overview",       testId: CASES_V2.tabOverview },
          { key: "contents",  label: "Contents",       testId: CASES_V2.tabContents },
          { key: "condition", label: "Condition",      testId: CASES_V2.tabCondition },
          { key: "serial",    label: "Serial / Power", testId: CASES_V2.tabSerial },
          { key: "password",  label: "Password",       testId: CASES_V2.tabPassword },
          { key: "photos",    label: "Photos",         testId: CASES_V2.tabPhotos },
          { key: "reverse",   label: "Reverse Pickup", testId: CASES_V2.tabReversePickup },
          { key: "workflow",  label: "Workflow",       testId: CASES_V2.tabWorkflow },
          { key: "dispatch",  label: "Dispatch",       testId: CASES_V2.tabDispatch },
          { key: "print",     label: "Print History",  testId: CASES_V2.tabPrint },
          { key: "timeline",  label: "Timeline",       testId: CASES_V2.tabTimeline },
        ]}
      />

      {tab === "overview"  && <Overview detail={detail} />}
      {tab === "contents"  && <Contents detail={detail} readOnly={readOnly} onSubmit={(items) => runAction(() => api.put(`/cases-v2/${detail.id}/contents`, { items }))} busy={busy} />}
      {tab === "condition" && <Condition detail={detail} readOnly={readOnly} runAction={runAction} busy={busy}
                                          onSubmit={(body) => runAction(() => api.put(`/cases-v2/${detail.id}/condition`, body))} />}
      {tab === "serial"    && <Serial    detail={detail} readOnly={readOnly} onSubmit={(body) => runAction(() => api.put(`/cases-v2/${detail.id}/serial`, body))} busy={busy} />}
      {tab === "password"  && <Password  detail={detail} readOnly={readOnly} onSubmit={(body) => runAction(() => api.put(`/cases-v2/${detail.id}/password`, body))} busy={busy} />}
      {tab === "photos"    && <Photos    detail={detail} readOnly={readOnly} onUploaded={load} busy={busy} setBusy={setBusy} setError={setError} />}
      {tab === "reverse"   && <ReversePickup detail={detail} readOnly={readOnly} onSubmit={(body) => runAction(() => api.put(`/cases-v2/${detail.id}/reverse-pickup`, body))} busy={busy} />}
      {tab === "workflow"  && <Workflow  detail={detail} readOnly={readOnly} runAction={runAction} busy={busy} />}
      {tab === "dispatch"  && <Dispatch  detail={detail} readOnly={readOnly} runAction={runAction} busy={busy} />}
      {tab === "print"     && <PrintHistory detail={detail} />}
      {tab === "timeline"  && <Timeline  detail={detail} />}

      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          data-testid="case-delete-dialog">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-slate-900">Delete Customer Case</h3>
            <p className="mt-2 text-sm text-slate-600">
              Delete this customer case? This action cannot be undone.
            </p>
            {deleteError && (
              <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700"
                data-testid="case-delete-error">
                {deleteError}
              </div>
            )}
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                data-testid="case-delete-cancel"
                onClick={() => { setConfirmDelete(false); setDeleteError(""); }}
                disabled={deleting}
                className={btnGhost}>
                Cancel
              </button>
              <button
                type="button"
                data-testid="case-delete-confirm"
                onClick={performDelete}
                disabled={deleting}
                className="inline-flex items-center gap-1 rounded-md bg-rose-600 px-3 py-2 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-40">
                <Trash2 size={14} /> {deleting ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------- Overview ------------------------- */
function Overview({ detail }) {
  const w = detail;
  const rows = [
    ["Case No", w.case_no],
    ["Created By", w.created_by_label],
    ["Customer Source", w.customer_source],
    ["Warranty", w.warranty_days == null ? "As per contract" : `${w.warranty_days} days · ends ${w.warranty_end_date}`],
    ["Customer", `${w.customer_name} · ${w.mobile}`],
    ["Alt", w.alternate_number || "—"],
    ["Email", w.customer_email || "—"],
    ["Company", w.company_name || "—"],
    ["GST", w.gst_number || "—"],
    ["Address", [w.address_line, w.city, w.state, w.pincode].filter(Boolean).join(", ") || "—"],
    ["Laptop", [w.brand, w.model].filter(Boolean).join(" ") || "—"],
    ["Serial", w.manufacturer_serial || "—"],
    ["Specs", [w.processor, w.ram, w.storage, w.generation].filter(Boolean).join(" · ") || "—"],
    ["Charger Included", w.charger_included == null ? "—" : (w.charger_included ? "Yes" : "No")],
    ["Power Status", w.laptop_power_status || "—"],
    ["Assigned To", w.assigned_department || "—"],
    ["Warehouse Status", w.warehouse_status || "—"],
    ["QC Result", w.qc_result || "—"],
    ["Battery Required", w.has_battery_problem ? "Yes (Reception)" : (w.requires_battery_test ? "Yes (QC)" : "No")],
    ["Dispatch", w.dispatch_mode ? `${w.dispatch_mode} · ${w.dispatch_status || ""}` : "—"],
    ["Complaint", w.complaint],
  ];
  return (
    <Section title="Overview">
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
        {rows.map(([k, v]) => (
          <div key={k} className="flex gap-3">
            <dt className="w-40 shrink-0 text-slate-500">{k}</dt>
            <dd className="text-slate-900 whitespace-pre-wrap break-words">{v || "—"}</dd>
          </div>
        ))}
      </dl>
    </Section>
  );
}

/* ------------------------- Contents ------------------------- */
// Stable key generator so React reconciliation doesn't misfire when a row
// is removed from the middle of the list. Test IDs still use the index
// (that's what playwright targets) — the key is a React-only concern.
const _uid = () => (typeof crypto !== "undefined" && crypto.randomUUID)
  ? crypto.randomUUID()
  : `c-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

function Contents({ detail, readOnly, onSubmit, busy }) {
  const [items, setItems] = useState(
    (detail.contents || []).map((c) => ({
      _uid: _uid(),
      item_type: c.item_type, custom_name: c.custom_name || "",
      quantity: c.quantity || 1, remarks: c.remarks || "",
    })),
  );
  const add = () => setItems((p) => [...p, { _uid: _uid(), item_type: "laptop", custom_name: "", quantity: 1, remarks: "" }]);
  const upd = (i, k, v) => setItems((p) => p.map((it, idx) => idx === i ? { ...it, [k]: v } : it));
  const rem = (i) => setItems((p) => p.filter((_, idx) => idx !== i));
  return (
    <Section title="Contents Received"
      right={!readOnly && (
        <div className="flex gap-2">
          <button data-testid={CASES_V2.contentAddBtn} onClick={add} className={btnGhost}><Plus size={14} /> Add</button>
          <button data-testid={CASES_V2.contentSave} disabled={busy}
            onClick={() => onSubmit(items.map(({ _uid: _, ...rest }) => rest))} className={btnPrimary}><Save size={14} /> Save</button>
        </div>
      )}>
      <div className="space-y-3">
        {items.length === 0 && <div className="text-sm text-slate-400">No items yet.</div>}
        {items.map((it, i) => (
          <div key={it._uid} className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-start">
            <select data-testid={CASES_V2.contentTypeSelect(i)} value={it.item_type}
              onChange={(e) => upd(i, "item_type", e.target.value)} className={`${inputCls} sm:col-span-3`}>
              {CONTENT_TYPES.map((t) => <option key={t} value={t}>{t.replaceAll("_", " ")}</option>)}
            </select>
            <input data-testid={CASES_V2.contentCustom(i)} value={it.custom_name}
              placeholder={it.item_type === "other" ? "Custom name" : "—"}
              disabled={it.item_type !== "other"}
              onChange={(e) => upd(i, "custom_name", e.target.value)} className={`${inputCls} sm:col-span-3`} />
            <input data-testid={CASES_V2.contentQty(i)} value={it.quantity} type="number" min={1}
              onChange={(e) => upd(i, "quantity", Number(e.target.value) || 1)} className={`${inputCls} sm:col-span-1`} />
            <input data-testid={CASES_V2.contentRemarks(i)} value={it.remarks} placeholder="Remarks"
              onChange={(e) => upd(i, "remarks", e.target.value)} className={`${inputCls} sm:col-span-4`} />
            {!readOnly && (
              <button data-testid={CASES_V2.contentRemove(i)} onClick={() => rem(i)}
                className="sm:col-span-1 text-rose-600 hover:text-rose-800 flex items-center justify-center py-2">
                <Trash2 size={14} />
              </button>
            )}
          </div>
        ))}
      </div>
    </Section>
  );
}

/* ------------------------- Condition ------------------------- */
function Condition({ detail, readOnly, onSubmit, runAction, busy }) {
  const [overall, setOverall] = useState(detail.overall_condition || "good");
  const [issues, setIssues] = useState(detail.condition_issues || []);
  const [remarks, setRemarks] = useState(detail.condition_remarks || "");
  const toggle = (v) => setIssues((p) => p.includes(v) ? p.filter((x) => x !== v) : [...p, v]);

  // Physical damage state.
  const [pd, setPd] = useState(!!detail.physical_damage);
  const [pdTypes, setPdTypes] = useState(detail.physical_damage_types || []);
  const [pdOther, setPdOther] = useState(detail.physical_damage_other || "");
  const [pdRemarks, setPdRemarks] = useState(detail.physical_damage_remarks || "");
  const togglePd = (v) => setPdTypes((p) => p.includes(v) ? p.filter((x) => x !== v) : [...p, v]);
  const photoCount = (detail.photos || []).length;
  const authFrozen = detail.warranty_authorization_status === "pending";

  const savePhysicalDamage = () => runAction(() => api.put(`/cases-v2/${detail.id}/physical-damage`, {
    physical_damage: pd,
    physical_damage_types: pd ? pdTypes : [],
    physical_damage_other: pd && pdTypes.includes("other") ? pdOther : null,
    physical_damage_remarks: pd ? pdRemarks : null,
  }));

  const pdCanSave = !pd || (
    pdRemarks.trim().length > 0
    && photoCount >= 3
    && (!pdTypes.includes("other") || pdOther.trim().length > 0)
  );

  return (
    <div className="space-y-4">
      <Section title="Overall Laptop Condition"
        right={!readOnly && (
          <button data-testid={CASES_V2.conditionSave} disabled={busy}
            onClick={() => onSubmit({ overall_condition: overall, condition_issues: issues, condition_remarks: remarks })}
            className={btnPrimary}><Save size={14} /> Save</button>
        )}>
        <div className="flex gap-6 items-center">
          {["good", "bad"].map((o) => (
            <label key={o} className="inline-flex items-center gap-2 text-sm">
              <input type="radio" name="oc" checked={overall === o} onChange={() => setOverall(o)}
                data-testid={`case-v2-condition-overall-${o}`}
                className="border-slate-300" />
              {o.toUpperCase()}
            </label>
          ))}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {CONDITION_ISSUES.map((iss) => (
            <label key={iss} className="inline-flex items-center gap-2 text-xs text-slate-700">
              <input type="checkbox" checked={issues.includes(iss)} onChange={() => toggle(iss)}
                data-testid={CASES_V2.conditionIssue(iss)}
                className="rounded border-slate-300" />
              {iss.replaceAll("_", " ")}
            </label>
          ))}
        </div>
        <textarea rows={3} value={remarks} data-testid={CASES_V2.conditionRemarks}
          onChange={(e) => setRemarks(e.target.value)}
          placeholder={overall === "bad" ? "Required — describe every defect in detail" : "Optional remarks"}
          className={inputCls} />
      </Section>

      <Section title="Physical Damage (Reception)"
        right={!readOnly && !authFrozen && (
          <button data-testid="case-v2-pd-save" disabled={busy || !pdCanSave}
            onClick={savePhysicalDamage}
            className={btnPrimary}><Save size={14} /> Save</button>
        )}>
        {authFrozen && (
          <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            Warranty Authorization is pending — physical damage details are frozen until a
            SUPER_ADMIN or ADMIN approves or rejects the warranty.
          </div>
        )}
        <div className="flex gap-6 items-center">
          {[{ v: false, l: "No" }, { v: true, l: "Yes" }].map((o) => (
            <label key={String(o.v)} className="inline-flex items-center gap-2 text-sm">
              <input type="radio" name="pd" checked={pd === o.v} onChange={() => setPd(o.v)}
                disabled={authFrozen}
                data-testid={`case-v2-pd-${o.v ? "yes" : "no"}`}
                className="border-slate-300" />
              {o.l}
            </label>
          ))}
        </div>
        {pd && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {PHYSICAL_DAMAGE_TYPES.map((t) => (
                <label key={t.v} className="inline-flex items-center gap-2 text-xs text-slate-700">
                  <input type="checkbox" checked={pdTypes.includes(t.v)}
                    onChange={() => togglePd(t.v)}
                    disabled={authFrozen}
                    data-testid={`case-v2-pd-type-${t.v}`}
                    className="rounded border-slate-300" />
                  {t.l}
                </label>
              ))}
            </div>
            {pdTypes.includes("other") && (
              <input value={pdOther} onChange={(e) => setPdOther(e.target.value)}
                disabled={authFrozen}
                data-testid="case-v2-pd-other"
                placeholder="Specify — required when 'Other' is selected"
                className={inputCls} />
            )}
            <textarea rows={3} value={pdRemarks} onChange={(e) => setPdRemarks(e.target.value)}
              disabled={authFrozen}
              data-testid="case-v2-pd-remarks"
              placeholder="Physical damage remarks * (required)"
              className={inputCls} />
            <div className={`text-xs ${photoCount >= 3 ? "text-emerald-700" : "text-rose-700"}`}>
              Photos uploaded: <b>{photoCount}</b> / 3 required
              {photoCount < 3 && " — upload at least 3 photos in the Photos tab before saving."}
            </div>
          </>
        )}
      </Section>
    </div>
  );
}

/* ------------------------- Serial ------------------------- */
function Serial({ detail, readOnly, onSubmit, busy }) {
  const [power, setPower] = useState(detail.laptop_power_status || "working");
  const [serial, setSerial] = useState(detail.manufacturer_serial || "");
  const needsSerial = power === "working";
  return (
    <Section title="Laptop Power & Serial"
      right={!readOnly && (
        <button data-testid={CASES_V2.serialSave} disabled={busy || (needsSerial && !serial.trim())}
          onClick={() => onSubmit({ laptop_power_status: power, manufacturer_serial: serial.trim() || null })}
          className={btnPrimary}><Save size={14} /> Save</button>
      )}>
      <div className="flex gap-6 items-center">
        {["working", "dead"].map((o) => (
          <label key={o} className="inline-flex items-center gap-2 text-sm">
            <input type="radio" name="ps" checked={power === o} onChange={() => setPower(o)}
              data-testid={`case-v2-power-status-${o}`}
              className="border-slate-300" />
            {o === "working" ? "Powers ON" : "Dead / No Power"}
          </label>
        ))}
      </div>
      <div>
        <div className="text-xs font-medium text-slate-500 mb-1">
          Manufacturer Serial {needsSerial && <span className="text-rose-500">* required (laptop powers on)</span>}
        </div>
        <input value={serial} data-testid={CASES_V2.serialInput}
          onChange={(e) => setSerial(e.target.value)} className={inputCls} />
        {!needsSerial && (
          <p className="text-xs text-slate-500 mt-1">
            Optional now — Warehouse must capture the serial once the laptop is opened.
          </p>
        )}
      </div>
    </Section>
  );
}

/* ------------------------- Password ------------------------- */
function Password({ detail, readOnly, onSubmit, busy }) {
  const ps = detail.password_section || {};
  const [win, setWin] = useState(ps.windows_password || "");
  const [pin, setPin] = useState(ps.pin || "");
  const [bios, setBios] = useState(ps.bios_password || "");
  const [remarks, setRemarks] = useState(ps.remarks || "");
  const [reveal, setReveal] = useState(false);
  return (
    <Section title="Password Section"
      right={
        <div className="flex items-center gap-2">
          <button data-testid={CASES_V2.passwordEye} type="button" onClick={() => setReveal((r) => !r)}
            className={btnGhost}>{reveal ? <><EyeOff size={14} /> Hide</> : <><Eye size={14} /> Reveal</>}</button>
          {!readOnly && (
            <button data-testid={CASES_V2.passwordSave} disabled={busy}
              onClick={() => onSubmit({ windows_password: win || null, pin: pin || null, bios_password: bios || null, remarks: remarks || null })}
              className={btnPrimary}><Save size={14} /> Save</button>
          )}
        </div>
      }>
      <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
        Never share these credentials with the customer. Hidden by default and only viewable by staff.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <div className="text-xs text-slate-500 mb-1">Windows Password</div>
          <input type={reveal ? "text" : "password"} value={win}
            data-testid={CASES_V2.passwordWin}
            onChange={(e) => setWin(e.target.value)} className={inputCls} />
        </div>
        <div>
          <div className="text-xs text-slate-500 mb-1">Windows PIN</div>
          <input type={reveal ? "text" : "password"} value={pin} inputMode="numeric"
            data-testid={CASES_V2.passwordPin}
            onChange={(e) => setPin(e.target.value)} className={inputCls} />
        </div>
        <div>
          <div className="text-xs text-slate-500 mb-1">BIOS Password</div>
          <input type={reveal ? "text" : "password"} value={bios}
            data-testid={CASES_V2.passwordBios}
            onChange={(e) => setBios(e.target.value)} className={inputCls} />
        </div>
      </div>
      <textarea rows={2} placeholder="Notes (e.g. hint, expiry, alternate account)" value={remarks}
        data-testid={CASES_V2.passwordRemarks}
        onChange={(e) => setRemarks(e.target.value)} className={inputCls} />
    </Section>
  );
}

/* ------------------------- Photos ------------------------- */
function Photos({ detail, readOnly, onUploaded, busy, setBusy, setError }) {
  const [angle, setAngle] = useState("front");
  const backendUrl = (process.env.REACT_APP_BACKEND_URL || "");
  const upload = async (fileList) => {
    if (!fileList?.[0]) return;
    setBusy(true); setError(null);
    try {
      const stored = getStoredAuth();
      const token = stored?.access_token;
      const form = new FormData();
      form.append("angle", angle);
      form.append("file", fileList[0]);
      const resp = await fetch(`${API_BASE}/cases-v2/${detail.id}/photos`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` }, body: form,
      });
      if (!resp.ok) throw new Error(`Upload failed (${resp.status})`);
      onUploaded();
    } catch (e) {
      setError(e.message || "Upload failed");
    } finally { setBusy(false); }
  };
  return (
    <Section title="Photos"
      right={!readOnly && (
        <div className="flex items-center gap-2">
          <select value={angle} onChange={(e) => setAngle(e.target.value)}
            data-testid={CASES_V2.photoAngle}
            className={inputCls + " w-auto"}>
            {PHOTO_ANGLES.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <label className={btnPrimary + " cursor-pointer"}>
            <Upload size={14} /> Upload
            <input type="file" accept="image/*" className="hidden"
              data-testid={CASES_V2.photoUpload}
              onChange={(e) => upload(e.target.files)} disabled={busy} />
          </label>
        </div>
      )}>
      {(detail.photos || []).length === 0 && (
        <div className="text-sm text-slate-400">No photos uploaded.</div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {(detail.photos || []).map((p, i) => (
          <a key={p.id || i} data-testid={CASES_V2.photoItem(i)}
             href={`${backendUrl}${p.file_path}`} target="_blank" rel="noreferrer"
             className="block rounded-md border border-slate-200 overflow-hidden hover:border-slate-400">
            <img src={`${backendUrl}${p.file_path}`} alt={p.angle}
              className="w-full h-32 object-cover" />
            <div className="px-2 py-1 text-xs text-slate-600 border-t bg-slate-50">{p.angle}</div>
          </a>
        ))}
      </div>
    </Section>
  );
}

/* ------------------------- Reverse Pickup ------------------------- */
function ReversePickup({ detail, readOnly, onSubmit, busy }) {
  const rp = detail.reverse_pickup || {};
  const [courier, setCourier] = useState(rp.courier_name || "");
  const [awb, setAwb] = useState(rp.awb_number || "");
  const [st, setSt] = useState(rp.status || "waiting_pickup");
  const applicable = ["amazon", "rentopc", "corporate", "other"].includes(detail.customer_source);
  return (
    <Section title="Online Reverse Pickup"
      right={!readOnly && applicable && (
        <button data-testid={CASES_V2.rpSave} disabled={busy}
          onClick={() => onSubmit({ courier_name: courier || null, awb_number: awb || null, status: st })}
          className={btnPrimary}><Save size={14} /> Save</button>
      )}>
      {!applicable ? (
        <p className="text-sm text-slate-500">Not applicable for {detail.customer_source} cases.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <div className="text-xs text-slate-500 mb-1">Courier</div>
            <input value={courier} onChange={(e) => setCourier(e.target.value)}
              data-testid={CASES_V2.rpCourier} className={inputCls} />
          </div>
          <div>
            <div className="text-xs text-slate-500 mb-1">AWB Number</div>
            <input value={awb} onChange={(e) => setAwb(e.target.value)}
              data-testid={CASES_V2.rpAwb} className={inputCls} />
          </div>
          <div>
            <div className="text-xs text-slate-500 mb-1">Status</div>
            <select value={st} onChange={(e) => setSt(e.target.value)}
              data-testid={CASES_V2.rpStatus} className={inputCls}>
              <option value="waiting_pickup">Waiting Pickup</option>
              <option value="in_transit">In Transit</option>
              <option value="received_at_reception">Received at Reception</option>
            </select>
          </div>
        </div>
      )}
    </Section>
  );
}

/* ------------------------- Workflow (Assign + Warehouse + QC + Battery) ------------------------- */
function Workflow({ detail, readOnly, runAction, busy }) {
  const [dept, setDept] = useState("warehouse");
  const [assignRemarks, setAssignRemarks] = useState("");

  const [whStatus, setWhStatus] = useState("diagnosis_started");
  const [whRemarks, setWhRemarks] = useState("");
  const [whSerial, setWhSerial] = useState(detail.manufacturer_serial || "");

  const [qcResult, setQcResult] = useState("passed");
  const [qcRemarks, setQcRemarks] = useState("");
  const [qcBattery, setQcBattery] = useState(false);

  const [batPresent, setBatPresent] = useState(true);
  const [batHealth, setBatHealth] = useState("good");
  const [batWh, setBatWh] = useState("");
  const [batCycles, setBatCycles] = useState("");
  const [batType, setBatType] = useState("");

  return (
    <div className="space-y-4">
      <Section title="Reception — Assign"
        right={!readOnly && (
          <button data-testid={CASES_V2.assignSave} disabled={busy}
            onClick={() => runAction(() => api.post(`/cases-v2/${detail.id}/assign`,
              { department: dept, remarks: assignRemarks || null }))}
            className={btnPrimary}><CheckCircle2 size={14} /> Assign</button>
        )}>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <select value={dept} onChange={(e) => setDept(e.target.value)}
            data-testid={CASES_V2.assignDept} className={inputCls}>
            <option value="warehouse">Warehouse (Diagnosis)</option>
            <option value="qc">QC Directly</option>
          </select>
          <input value={assignRemarks} onChange={(e) => setAssignRemarks(e.target.value)}
            data-testid={CASES_V2.assignRemarks}
            placeholder="Remarks" className={`${inputCls} sm:col-span-2`} />
        </div>
      </Section>

      <Section title="Warehouse"
        right={!readOnly && (
          <button data-testid={CASES_V2.whSave} disabled={busy || !whRemarks.trim()}
            onClick={() => runAction(() => api.post(`/cases-v2/${detail.id}/warehouse`,
              { status: whStatus, remarks: whRemarks, manufacturer_serial: whSerial || null }))}
            className={btnPrimary}><Save size={14} /> Update</button>
        )}>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <select value={whStatus} onChange={(e) => setWhStatus(e.target.value)}
            data-testid={CASES_V2.whStatus} className={inputCls}>
            {WAREHOUSE_STATUSES.map((s) => <option key={s} value={s}>{s.replaceAll("_", " ")}</option>)}
          </select>
          <input value={whSerial} onChange={(e) => setWhSerial(e.target.value)}
            data-testid={CASES_V2.whSerial}
            placeholder="Manufacturer Serial (if just opened)" className={inputCls} />
          <input value={whRemarks} onChange={(e) => setWhRemarks(e.target.value)}
            data-testid={CASES_V2.whRemarks}
            placeholder="Remarks (required)" className={inputCls} />
        </div>
        <p className="text-xs text-slate-500">
          Non repairable / warranty rejected auto-routes back to Reception, skipping Battery.
        </p>
      </Section>

      <Section title="QC"
        right={!readOnly && (
          <button data-testid={CASES_V2.qcSave} disabled={busy || !qcRemarks.trim()}
            onClick={() => runAction(() => api.post(`/cases-v2/${detail.id}/qc`,
              { result: qcResult, remarks: qcRemarks, requires_battery_test: detail.has_battery_problem ? undefined : qcBattery }))}
            className={btnPrimary}><Save size={14} /> Update</button>
        )}>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <select value={qcResult} onChange={(e) => setQcResult(e.target.value)}
            data-testid={CASES_V2.qcResult} className={inputCls}>
            <option value="passed">Pass</option>
            <option value="failed">Fail</option>
            <option value="return_to_warehouse">Return to Warehouse</option>
          </select>
          <input value={qcRemarks} onChange={(e) => setQcRemarks(e.target.value)}
            data-testid={CASES_V2.qcRemarks}
            placeholder="Remarks (required)" className={`${inputCls} sm:col-span-2`} />
        </div>
        {!detail.has_battery_problem && (
          <label className="inline-flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={qcBattery} data-testid={CASES_V2.qcBatteryToggle}
              onChange={(e) => setQcBattery(e.target.checked)} className="rounded border-slate-300" />
            Requires Battery Test
          </label>
        )}
        {detail.has_battery_problem && (
          <p className="text-xs text-fuchsia-700">
            Reception flagged Battery Problem — passing QC will route directly to Battery Engineer.
          </p>
        )}
      </Section>

      <Section title="Battery Engineer"
        right={!readOnly && (
          <button data-testid={CASES_V2.batSave} disabled={busy}
            onClick={() => runAction(() => api.post(`/cases-v2/${detail.id}/battery`, {
              battery_present: batPresent,
              battery_health: batHealth || null,
              battery_capacity_wh: batWh ? Number(batWh) : null,
              battery_cycle_count: batCycles ? Number(batCycles) : null,
              battery_type: batType || null,
            }))}
            className={btnPrimary}><Save size={14} /> Complete Battery</button>
        )}>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={batPresent} data-testid={CASES_V2.batPresent}
              onChange={(e) => setBatPresent(e.target.checked)} className="rounded border-slate-300" />
            Battery Present
          </label>
          <select value={batHealth} onChange={(e) => setBatHealth(e.target.value)}
            data-testid={CASES_V2.batHealth} className={inputCls}>
            {["good", "average", "poor", "replaced"].map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
          <input value={batType} onChange={(e) => setBatType(e.target.value)}
            data-testid={CASES_V2.batType}
            placeholder="Battery Type (Li-Ion, LiPo, …)" className={inputCls} />
          <input value={batWh} type="number" step="0.1"
            data-testid={CASES_V2.batWh}
            onChange={(e) => setBatWh(e.target.value)} placeholder="Capacity (Wh)" className={inputCls} />
          <input value={batCycles} type="number"
            data-testid={CASES_V2.batCycles}
            onChange={(e) => setBatCycles(e.target.value)} placeholder="Cycle Count" className={inputCls} />
        </div>
      </Section>
    </div>
  );
}

/* ------------------------- Dispatch + Close ------------------------- */
function Dispatch({ detail, readOnly, runAction, busy }) {
  const [mode, setMode] = useState(detail.dispatch_mode || "walkin");
  const [courier, setCourier] = useState(detail.dispatch_courier_name || "");
  const [awb, setAwb] = useState(detail.dispatch_awb || "");
  const [labelPath, setLabelPath] = useState(detail.dispatch_label_path || "");
  const [porter, setPorter] = useState(detail.dispatch_porter_name || "");
  const [uncle, setUncle] = useState(detail.dispatch_uncle_by || "");
  const [driver, setDriver] = useState(detail.dispatch_vehicle_driver || "");
  const [sig, setSig] = useState(detail.customer_signature_path || "");
  const [remarks, setRemarks] = useState("");
  const [closeR, setCloseR] = useState("");

  const build = () => {
    const p = { dispatch_mode: mode, remarks: remarks || null };
    if (mode === "walkin") p.customer_signature_path = sig || null;
    if (mode === "courier") { p.courier_name = courier; p.awb_number = awb; p.dispatch_label_path = labelPath || null; }
    if (mode === "porter") p.porter_name = porter;
    if (mode === "uncle") p.delivered_by = uncle;
    if (mode === "company_vehicle") p.vehicle_driver = driver;
    return p;
  };

  const canDispatch = () => {
    if (mode === "walkin") return !!sig.trim();
    if (mode === "courier") return !!courier.trim() && !!awb.trim();
    if (mode === "porter") return !!porter.trim();
    if (mode === "uncle") return !!uncle.trim();
    if (mode === "company_vehicle") return !!driver.trim();
    return true;
  };

  return (
    <div className="space-y-4">
      <Section title="Dispatch"
        right={!readOnly && detail.status !== "CLOSED" && (
          <button data-testid={CASES_V2.dispatchSave} disabled={busy || !canDispatch()}
            onClick={() => runAction(() => api.post(`/cases-v2/${detail.id}/dispatch`, build()))}
            className={btnPrimary}><Save size={14} /> Record Dispatch</button>
        )}>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <select value={mode} onChange={(e) => setMode(e.target.value)}
            data-testid={CASES_V2.dispatchMode} className={inputCls}>
            {DISPATCH_MODES.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
          </select>
          {mode === "walkin" && (
            <input value={sig} onChange={(e) => setSig(e.target.value)}
              data-testid={CASES_V2.dispatchSignature}
              placeholder="Signature file path or acknowledgement note *" className={`${inputCls} sm:col-span-2`} />
          )}
          {mode === "courier" && (
            <>
              <input value={courier} onChange={(e) => setCourier(e.target.value)}
                data-testid={CASES_V2.dispatchCourierName}
                placeholder="Courier Name *" className={inputCls} />
              <input value={awb} onChange={(e) => setAwb(e.target.value)}
                data-testid={CASES_V2.dispatchAwb}
                placeholder="AWB Number *" className={inputCls} />
              <input value={labelPath} onChange={(e) => setLabelPath(e.target.value)}
                data-testid={CASES_V2.dispatchLabel}
                placeholder="Label upload URL (optional)" className={`${inputCls} sm:col-span-2`} />
            </>
          )}
          {mode === "porter" && (
            <input value={porter} onChange={(e) => setPorter(e.target.value)}
              data-testid={CASES_V2.dispatchPorter}
              placeholder="Porter Name *" className={`${inputCls} sm:col-span-2`} />
          )}
          {mode === "uncle" && (
            <input value={uncle} onChange={(e) => setUncle(e.target.value)}
              data-testid={CASES_V2.dispatchUncle}
              placeholder="Delivered By *" className={`${inputCls} sm:col-span-2`} />
          )}
          {mode === "company_vehicle" && (
            <input value={driver} onChange={(e) => setDriver(e.target.value)}
              data-testid={CASES_V2.dispatchVehicle}
              placeholder="Vehicle / Driver Name *" className={`${inputCls} sm:col-span-2`} />
          )}
        </div>
        <input value={remarks} onChange={(e) => setRemarks(e.target.value)}
          data-testid={CASES_V2.dispatchRemarks}
          placeholder="Remarks" className={inputCls} />
        {detail.status === "SHIPPED" && (
          <button data-testid={CASES_V2.markDelivered} disabled={busy}
            onClick={() => runAction(() => api.post(`/cases-v2/${detail.id}/courier-delivered`, { remarks: null }))}
            className={btnGhost}><CheckCircle2 size={14} /> Mark Delivered</button>
        )}
      </Section>

      {detail.status !== "CLOSED" && (
        <Section title="Close Case"
          right={
            <button data-testid={CASES_V2.closeBtn} disabled={busy || !closeR.trim()}
              onClick={() => runAction(() => api.post(`/cases-v2/${detail.id}/close`, { closing_remarks: closeR }))}
              className={btnPrimary}><CheckCircle2 size={14} /> Close</button>
          }>
          <input value={closeR} onChange={(e) => setCloseR(e.target.value)}
            data-testid={CASES_V2.closeRemarks}
            placeholder="Closing remarks (required)" className={inputCls} />
        </Section>
      )}
    </div>
  );
}

/* ------------------------- Print History + Timeline ------------------------- */
function PrintHistory({ detail }) {
  const rows = detail.print_history || [];
  return (
    <Section title={`Print History (${rows.length})`}>
      {rows.length === 0 && <div className="text-sm text-slate-400">No prints yet.</div>}
      <ul className="text-sm space-y-1">
        {rows.map((p, i) => (
          <li key={p.id || i} data-testid={CASES_V2.printHistoryRow(i)}
              className="flex items-center gap-3 border-b border-slate-100 py-2">
            <span className="text-xs font-mono text-slate-500">{new Date(p.printed_at).toLocaleString()}</span>
            <span className="uppercase text-xs">{p.print_type}</span>
            {p.is_reprint && <span className="text-xs text-amber-700 bg-amber-50 px-2 py-0.5 rounded">REPRINT</span>}
          </li>
        ))}
      </ul>
    </Section>
  );
}

function Timeline({ detail }) {
  const rows = detail.timeline || [];
  return (
    <Section title={`Timeline (${rows.length})`}>
      {rows.length === 0 && <div className="text-sm text-slate-400">No events yet.</div>}
      <ol data-testid={CASES_V2.timeline} className="text-sm space-y-2">
        {rows.map((e, i) => (
          <li key={e.id || i} data-testid={CASES_V2.timelineRow(i)}
              className="flex gap-3 border-l-2 border-slate-200 pl-3">
            <span className="text-xs font-mono text-slate-500">{new Date(e.event_at).toLocaleString()}</span>
            <div>
              <div className="text-slate-900 font-medium">{e.event_code}</div>
              {e.message && e.message !== e.event_code && (
                <div className="text-xs text-slate-500">{e.message}</div>
              )}
            </div>
          </li>
        ))}
      </ol>
    </Section>
  );
}

/* ------------------------- Warranty Authorization ------------------------- */
function WarrantyAuthorizationCard({ detail, user, busy, runAction }) {
  const [remarks, setRemarks] = useState("");
  const status = detail.warranty_authorization_status;
  const canDecide = status === "pending"
    && (user?.role === "SUPER_ADMIN" || user?.role === "ADMIN");

  if (status === "approved" || status === "rejected") {
    const cls = status === "approved"
      ? "border-emerald-300 bg-emerald-50 text-emerald-800"
      : "border-rose-300 bg-rose-50 text-rose-800";
    return (
      <section data-testid="case-v2-warranty-auth-card"
        className={`rounded-lg border ${cls} p-4 shadow-sm`}>
        <div className="text-sm font-semibold">
          Warranty {status === "approved" ? "Approved" : "Rejected — Converted to Paid Service"}
        </div>
        <div className="text-xs mt-1">
          {detail.warranty_authorized_at && (
            <>On {new Date(detail.warranty_authorized_at).toLocaleString()} · </>
          )}
          By: <span className="font-mono">{detail.warranty_authorized_by || "—"}</span>
        </div>
        {detail.warranty_authorization_remarks && (
          <div className="text-xs mt-1 italic">"{detail.warranty_authorization_remarks}"</div>
        )}
      </section>
    );
  }

  return (
    <section data-testid="case-v2-warranty-auth-card"
      className="rounded-lg border border-amber-300 bg-amber-50 p-4 shadow-sm space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-amber-900">
            Warranty Authorization Pending
          </div>
          <div className="text-xs text-amber-800 mt-1">
            Physical damage was reported on an in-warranty case. Reception, Warehouse, QC and
            Battery Engineer cannot change the warranty decision until a SUPER_ADMIN or ADMIN
            {" "}approves or rejects here.
          </div>
        </div>
      </div>
      {canDecide ? (
        <>
          <textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)}
            data-testid="case-v2-warranty-auth-remarks"
            placeholder="Authorization remarks * (required)"
            className={inputCls} />
          <div className="flex items-center gap-2">
            <button data-testid="case-v2-warranty-approve" disabled={busy || !remarks.trim()}
              onClick={() => runAction(() => api.post(`/cases-v2/${detail.id}/warranty-authorization`, {
                decision: "approved", remarks: remarks.trim(),
              }))}
              className="inline-flex items-center gap-1 rounded-md bg-emerald-700 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-800 disabled:opacity-40">
              Approve Warranty
            </button>
            <button data-testid="case-v2-warranty-reject" disabled={busy || !remarks.trim()}
              onClick={() => runAction(() => api.post(`/cases-v2/${detail.id}/warranty-authorization`, {
                decision: "rejected", remarks: remarks.trim(),
              }))}
              className="inline-flex items-center gap-1 rounded-md bg-rose-700 px-3 py-2 text-sm font-medium text-white hover:bg-rose-800 disabled:opacity-40">
              Reject Warranty (Convert to Paid Service)
            </button>
          </div>
        </>
      ) : (
        <div className="text-xs text-amber-800">
          Only SUPER_ADMIN or ADMIN can decide. Your role: <b>{user?.role || "—"}</b>.
        </div>
      )}
    </section>
  );
}
